"use client"

import { FormikProvider, useFormik } from "formik"
import { FolderPlus, Loader2 } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { FormInput } from "@/components/form-inputs"
import { useCreateKnowledgeBaseFolderMutation } from "@/hooks/mutations/knowledge-base-folders"
import type {
  CreateKnowledgeBaseFolderInput,
  CreateKnowledgeBaseFolderResult,
} from "@/server-actions/knowledge-base-folders"
import { useTranslations } from "next-intl"


type CreateFolderDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreated?: (result: CreateKnowledgeBaseFolderResult) => void | Promise<void>
}

const INITIAL_VALUES: CreateKnowledgeBaseFolderInput = {
  name: "",
}

export function CreateFolderDialog({
  open,
  onOpenChange,
  onCreated,
}: CreateFolderDialogProps) {
  const tUi = useTranslations("Ai.AiKnowledgeBaseCreateFolderDialog")
  const tCommonUi = useTranslations("Common")

  const createFolder = useCreateKnowledgeBaseFolderMutation()

  const formik = useFormik<CreateKnowledgeBaseFolderInput>({
    initialValues: INITIAL_VALUES,
    enableReinitialize: true,
    onSubmit: async (values, helpers) => {
      try {
        const result = await createFolder.mutateAsync({
          ...values,
          setErrors: helpers.setErrors as (errors: Record<string, unknown>) => void,
        })
        onOpenChange(false)
        helpers.resetForm()
        void Promise.resolve(onCreated?.(result)).catch((error) => {
          console.error("[Knowledge Base] Failed to refresh after creating folder:", error)
        })
      } catch {
        // withQueryHandling already shows the error toast and maps backend validation errors.
      }
    },
  })

  const isSubmitting = createFolder.isPending || formik.isSubmitting
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      formik.resetForm()
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="space-y-5">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderPlus className="h-5 w-5" />
                <span><span key={tUi("createFolder")}>{tUi("createFolder")}</span></span>
              </DialogTitle>
              <DialogDescription>
                <span key={tUi("groupRelatedKnowledgeBaseDocumentsAndAssignThe")}>{tUi("groupRelatedKnowledgeBaseDocumentsAndAssignThe")}</span></DialogDescription>
            </DialogHeader>

            <FormInput
              name="name"
              label={tUi("folderName")}
              placeholder={tUi("websitePagesSupportDocs")}
              maxLength={120}
              showLength
              autoFocus
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={isSubmitting}
              >
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <FolderPlus className="mr-2 h-4 w-4" />
                )}
                <span><span key={tUi("createFolder")}>{tUi("createFolder")}</span></span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}
